import type DxfArrayScanner from '../DxfArrayScanner';
import { serializeGroupValue } from '../DxfArrayScanner';
import type { GroupValue } from '../DxfArrayScanner';
import {
  checkCommonEntityProperties,
  parsePoint,
  serializePoint,
  serializeCommonEntityProperty,
} from '../ParseHelpers';
import type IGeometry from './geometry';
import type { IEntity, IPoint } from './geometry';
import { EntityName } from './geometry';

export interface ITextEntity extends IEntity {
  type: EntityName.Text;
  firstAlignment: IPoint;
  secondAlignment: IPoint;
  style: string;
  textHeight: number;
  thickness: number; //default is 0
  xScale: number;
  rotation: number; //degrees
  text: string;
  halign: number;
  valign: number;
  extrusionDirection: IPoint;
}

const textPropertyFromCode = new Map<number, keyof ITextEntity>([
  [1, 'text'],
  [7, 'style'],
  [39, 'thickness'],
  [40, 'textHeight'],
  [41, 'xScale'],
  [50, 'rotation'],
  [70, 'standardFlags'],
  // NOTE: 72 and 73 are meaningless without 11 (second alignment point)
  [72, 'halign'],
  [73, 'valign'],
]);

const codeFromTextProperty = new Map<keyof ITextEntity, number>(
  Array.from(textPropertyFromCode.entries()).map(([code, property]) => [
    property,
    code,
  ]),
);

export default class Text implements IGeometry<ITextEntity> {
  public ForEntityName = EntityName.Text;
  public parseEntity(scanner: DxfArrayScanner): ITextEntity {
    const entity = { type: this.ForEntityName } as ITextEntity;
    let curr = scanner.next();
    while (!scanner.isEOF()) {
      if (curr.code === 0) break;
      const property = textPropertyFromCode.get(curr.code);
      if (property != null) {
        (entity[property] as GroupValue) = curr.value;
      } else {
        //special cases
        switch (curr.code) {
          case 10: // X coordinate of 'first alignment point'
            entity.firstAlignment = parsePoint(scanner);
            break;
          case 11: // X coordinate of 'second alignment point'
            entity.secondAlignment = parsePoint(scanner);
            break;
          case 210:
            entity.extrusionDirection = parsePoint(scanner);
            break;
          default:
            // check common entity attributes
            checkCommonEntityProperties(entity, curr, scanner);
            break;
        }
      }
      curr = scanner.next();
    }
    return entity;
  }

  public *serializeEntity(entity: ITextEntity): IterableIterator<string> {
    for (const [property, value] of Object.entries(entity) as [
      keyof ITextEntity,
      ITextEntity[keyof ITextEntity],
    ][]) {
      const code = codeFromTextProperty.get(property);
      if (code != null) {
        yield* serializeGroupValue(code, value as string | number);
      } else {
        //special cases
        switch (property) {
          case 'firstAlignment':
            yield* serializePoint(entity.firstAlignment, 10);
            break;
          case 'secondAlignment':
            yield* serializePoint(entity.secondAlignment, 11);
            break;
          case 'extrusionDirection':
            yield* serializePoint(value as IPoint, 210);
            break;
          default:
            yield* serializeCommonEntityProperty(
              property,
              value as string | number | boolean,
              entity,
            );
            break;
        }
      }
    }
  }
}
